import styled, { css } from 'styled-components'
import { Typography } from '@equinor/eds-core-react'
import { baseFont } from '@/styles'

// Picks black or white text for a coloured badge based on the background's perceived
// brightness, so numbers stay legible on both light and dark segment colours.
const getReadableTextColor = (hex: string): string => {
    const normalized = hex.replace('#', '')
    const r = parseInt(normalized.slice(0, 2), 16)
    const g = parseInt(normalized.slice(2, 4), 16)
    const b = parseInt(normalized.slice(4, 6), 16)
    const brightness = (r * 299 + g * 587 + b * 114) / 1000
    return brightness > 150 ? '#3d3d3d' : '#ffffff'
}

// Shared "menu-style" grey hover highlight used by clickable labels and the total value.
// Negative margins cancel the padding so the highlight box grows without shifting layout.
const hoverHighlight = css`
    padding: 4px 8px;
    margin: -4px -8px;
    border-radius: 4px;
    transition: background-color 0.15s ease;
`

export const PageContainer = styled.div`
    ${baseFont}
    display: flex;
    flex-direction: column;
    gap: 20px;
    background-color: #f7f7f7;
    min-height: 100%;
    padding: 24px;
    box-sizing: border-box;
`

export const PageTitle = styled(Typography)`
    color: #3d3d3d;
    font-size: 32px;
    font-weight: 400;
`

export const CardGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 20px;
    align-items: stretch;

    @media (max-width: 1300px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
`

export const Card = styled.div`
    background: #ffffff;
    border-radius: 6px;
    padding: 20px 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    height: 100%;
    box-sizing: border-box;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.24);
`

export const CardHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0;
`

export const CardTitle = styled(Typography)`
    color: #3d3d3d;
    font-family: Equinor;
    font-size: 18px;
    font-weight: 500;
`

export const CardSubtitle = styled(Typography)`
    color: #3d3d3d;
`

export const StatCard = styled(Card)`
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
`

export const WideCard = styled(Card)`
    grid-column: span 2;

    @media (max-width: 1300px) {
        grid-column: span 1;
    }
`

export const SensorTypeList = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 14px 28px;
    align-content: start;
`

export const StatTextWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`

export const StatValue = styled.button`
    ${baseFont}
    ${hoverHighlight}
    font-size: 32px;
    line-height: 1;
    font-weight: 400;
    color: #1c1c1c;
    background: none;
    border: none;
    text-align: left;
    cursor: pointer;

    &:hover {
        background: #eaeaea;
    }
`

export const ChartRow = styled.div`
    display: flex;
    justify-content: center;
    padding: 8px 0 4px;
`

export const Legend = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
    gap: 12px 16px;
    align-content: start;
`

export const LegendCompact = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(96px, 1fr));
    justify-items: start;
    gap: 14px 16px;
    align-content: start;
`

export const LegendItem = styled.button<{ $clickable: boolean }>`
    ${baseFont}
    ${hoverHighlight}
    display: flex;
    align-items: center;
    gap: 8px;
    background: none;
    border: none;
    text-align: left;
    cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

    &:hover {
        background: ${({ $clickable }) => ($clickable ? '#eaeaea' : 'none')};
    }
`

export const LegendBadge = styled.span<{ $color: string }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 20px;
    padding: 0 5px;
    border-radius: 3px;
    background: ${({ $color }) => $color};
    color: ${({ $color }) => getReadableTextColor($color)};
    font-size: 12px;
    font-weight: 500;
`

export const LegendLabel = styled.span`
    color: #3d3d3d;
    font-size: 14px;
    white-space: nowrap;
`

export const Message = styled(Typography)`
    color: #6f6f6f;
`

export const DesktopMessage = styled(Typography)`
    color: #3d3d3d;
    font-weight: 400;
`
